'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CountryCardProps {
  country: {
    id: string;
    name: string;
    flag: string;
    flagAlt: string;
    heroImage: string;
    heroImageAlt: string;
    tagline: string;
    highlights: string[];
    currency: string;
    currencySymbol: string;
    studentCount: number;
    topUniversities: number;
  };
}

export default function CountryCard({ country }: CountryCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative bg-card rounded-xl overflow-hidden border border-border hover:border-primary transition-all duration-300 hover:shadow-elevated"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={country.heroImage}
          alt={country.heroImageAlt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        <div className="absolute top-4 left-4 flex items-center space-x-2 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full">
          <AppImage
            src={country.flag}
            alt={country.flagAlt}
            className="w-6 h-6 rounded-full object-cover"
          />
          <span className="text-sm font-semibold text-foreground">{country.name}</span>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-bold text-white mb-2">{country.tagline}</h3>
          <div className="flex items-center space-x-4 text-white/90 text-sm">
            <div className="flex items-center space-x-1">
              <Icon name="AcademicCapIcon" size={16} variant="solid" className="text-white" />
              <span>{country.topUniversities}+ Universities</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="UserGroupIcon" size={16} variant="solid" className="text-white" />
              <span>{country.studentCount.toLocaleString()} Students</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-3 mb-4">
          {country.highlights.map((highlight, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-conversion flex-shrink-0 mt-0.5" />
              <span className="text-sm text-foreground">{highlight}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Currency: <span className="font-semibold text-foreground">{country.currencySymbol} {country.currency}</span>
          </div>
          <Link
            href={`/destinations/${country.id}`}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium text-sm"
          >
            <span>Explore</span>
            <Icon name="ArrowRightIcon" size={16} className="text-current" />
          </Link>
        </div>
      </div>
    </div>
  );
}